import React, { useState } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { ChevronDown } from 'lucide-react';

interface SalesDataPoint {
  date: string;
  totalSale: number;
  previousPeriod: number;
}

const defaultData: SalesDataPoint[] = [
  { date: '15 Aug', totalSale: 6500, previousPeriod: 4500 },
  { date: '16 Aug', totalSale: 8500, previousPeriod: 6800 },
  { date: '17 Aug', totalSale: 12500, previousPeriod: 5200 },
  { date: '18 Aug', totalSale: 10200, previousPeriod: 9000 },
  { date: '19 Aug', totalSale: 12400, previousPeriod: 8200 },
  { date: '20 Aug', totalSale: 11000, previousPeriod: 7500 },
  { date: '21 Aug', totalSale: 15200, previousPeriod: 13000 },
];

export const SalesOverviewChart: React.FC = () => {
  const [period, setPeriod] = useState<'Daily' | 'Weekly' | 'Monthly'>('Daily');

  // Custom Dot component for highlighting the active/latest point
  const CustomActiveDot = (props: any) => {
    const { cx, cy, index, dataLength } = props;
    if (index === dataLength - 1) {
      return (
        <g key="active-dot">
          <circle cx={cx} cy={cy} r={6} fill="#C1272D" stroke="#FFFFFF" strokeWidth={2} />
        </g>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-2xl p-6 border border-[#ECECF2] shadow-sm flex flex-col justify-between h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-base font-bold text-slate-800">Sales Overview</h3>
        
        {/* Period Dropdown */}
        <div className="relative group">
          <button className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
            {period}
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          </button>
          <div className="absolute right-0 mt-1 w-28 bg-white border border-slate-100 rounded-lg shadow-lg hidden group-hover:block z-10">
            {(['Daily', 'Weekly', 'Monthly'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className="w-full text-left px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg"
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Legend Indicators */}
      <div className="flex items-center justify-end gap-6 mb-4 text-xs font-medium text-slate-500">
        <div className="flex items-center gap-2">
          <span className="w-5 h-0.5 bg-[#C1272D] rounded-full inline-block" />
          <span>Total Sale</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-5 h-0.5 border-b-2 border-dashed border-rose-400 inline-block" />
          <span>Previous Period</span>
        </div>
      </div>

      {/* Chart Canvas */}
      <div className="w-full h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={defaultData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="totalSaleGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#C1272D" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#C1272D" stopOpacity={0.0} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />

            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#94A3B8', fontSize: 11, fontWeight: 500 }}
              dy={10}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#94A3B8', fontSize: 11, fontWeight: 500 }}
              tickFormatter={(val) => (val === 0 ? '₹0' : `₹${val / 1000}K`)}
              domain={[0, 20000]}
              ticks={[0, 5000, 10000, 15000, 20000]}
            />

            <Tooltip
              contentStyle={{
                backgroundColor: '#1E293B',
                borderRadius: '12px',
                border: 'none',
                color: '#FFFFFF',
                fontSize: '12px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              }}
              formatter={(value: any, name: any) => [
                `₹${Number(value).toLocaleString('en-IN')}`,
                name === 'totalSale' ? 'Total Sale' : 'Previous Period',
              ]}
              labelStyle={{ color: '#94A3B8', fontWeight: 600, marginBottom: '4px' }}
            />

            {/* Gradient Area Fill for Total Sale */}
            <Area
              type="monotone"
              dataKey="totalSale"
              stroke="#C1272D"
              strokeWidth={2.5}
              fillOpacity={1}
              fill="url(#totalSaleGradient)"
              activeDot={(props) => (
                <CustomActiveDot {...props} dataLength={defaultData.length} />
              )}
            />

            {/* Dashed Line for Previous Period */}
            <Line
              type="monotone"
              dataKey="previousPeriod"
              stroke="#FB7185"
              strokeWidth={2}
              strokeDasharray="4 4"
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
